/**
 * LaunchProgressBar — Global top bar that shows when software is launching
 * 
 * Listens for GENOFX_SOFTWARE_LAUNCH_REQUEST events.
 * Polls /api/software/sessions to track launch status.
 * Shows progress bar at the top of the page.
 * Shows error toast on failure.
 */
import { HugeiconsIcon } from '@hugeicons/react';
import { AlertCircleIcon as AlertCircle, Cancel01Icon as X, CheckmarkCircle02Icon as CheckCircle2, Loading03Icon as Loader2 } from '@hugeicons/core-free-icons';
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useAuthFetch } from '@/hooks/useAuthFetch';

interface LaunchState {
  softwareName: string;
  softwareSlug: string;
  status: 'launching' | 'running' | 'failed' | 'idle';
  sessionId?: string;
  error?: string;
}

export function LaunchProgressBar() {
  const [launch, setLaunch] = useState<LaunchState>({ softwareName: '', softwareSlug: '', status: 'idle' });
  const authFetch = useAuthFetch();

  // Listen for launch requests
  useEffect(() => {
    const handleLaunchRequest = (e: CustomEvent) => {
      const { softwareName, softwareSlug } = e.detail || {};
      if (softwareSlug) {
        setLaunch({ softwareName: softwareName || softwareSlug, softwareSlug, status: 'launching' });
      }
    };

    window.addEventListener('GENOFX_SOFTWARE_LAUNCH_REQUEST', handleLaunchRequest as EventListener);
    return () => window.removeEventListener('GENOFX_SOFTWARE_LAUNCH_REQUEST', handleLaunchRequest as EventListener);
  }, []);

  // Poll for session status when launching
  useEffect(() => {
    if (launch.status !== 'launching') return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 60; // 60 * 2s = 2 min max wait

    const poll = async () => {
      if (cancelled) return;
      attempts++;

      try {
        const res = await authFetch('/api/software/sessions');
        if (res.ok) {
          const data = await res.json();
          const sessions = data.sessions || [];
          const matchingSession = sessions.find(
            (s: any) => s.software_slug === launch.softwareSlug
          );

          if (matchingSession) {
            if (matchingSession.status === 'running') {
              setLaunch(prev => ({ ...prev, status: 'running', sessionId: matchingSession.id }));
              toast.success(`${launch.softwareName} is ready`);
              // Auto-hide after 3s
              setTimeout(() => setLaunch(prev => ({ ...prev, status: 'idle' })), 3000);
              return;
            }
            if (matchingSession.status === 'failed') {
              setLaunch(prev => ({ ...prev, status: 'failed', error: 'Session failed to start' }));
              toast.error(`${launch.softwareName} failed to launch`);
              return;
            }
          }
        }
      } catch {
        // Silent — keep polling
      }

      if (attempts >= maxAttempts) {
        setLaunch(prev => ({ ...prev, status: 'failed', error: 'Launch timed out' }));
        toast.error(`${launch.softwareName} launch timed out`);
        return;
      }

      if (!cancelled) {
        setTimeout(poll, 2000);
      }
    };

    // Start polling after 1s (give launch API time to respond)
    const timer = setTimeout(poll, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [launch.status, launch.softwareSlug, launch.softwareName, authFetch]);

  const dismiss = () => setLaunch(prev => ({ ...prev, status: 'idle' }));

  if (launch.status === 'idle') return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        className="fixed top-0 left-0 right-0 z-[200]"
      >
        <div className={`h-10 flex items-center justify-center gap-2 text-sm font-medium px-4 ${
          launch.status === 'launching' ? 'bg-blue-500/10 border-b border-blue-500/20 text-blue-700 dark:text-blue-300' :
          launch.status === 'running' ? 'bg-green-500/10 border-b border-green-500/20 text-green-700 dark:text-green-300' :
          'bg-red-500/10 border-b border-red-500/20 text-red-700 dark:text-red-300'
        }`}>
          {launch.status === 'launching' && (
            <>
              <HugeiconsIcon icon={Loader2} className="h-4 w-4 animate-spin"  />
              <span>Launching {launch.softwareName}...</span>
            </>
          )}
          {launch.status === 'running' && (
            <>
              <HugeiconsIcon icon={CheckCircle2} className="h-4 w-4"  />
              <span>{launch.softwareName} is ready</span>
            </>
          )}
          {launch.status === 'failed' && (
            <>
              <HugeiconsIcon icon={AlertCircle} className="h-4 w-4"  />
              <span>{launch.softwareName} failed to launch</span>
              {launch.error && <span className="text-xs opacity-70">— {launch.error}</span>}
            </>
          )}
          <button onClick={dismiss} className="ml-auto p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded">
            <HugeiconsIcon icon={X} className="h-3.5 w-3.5"  />
          </button>
        </div>

        {/* Progress bar animation for launching state */}
        {launch.status === 'launching' && (
          <div className="h-0.5 w-full bg-blue-200/30 overflow-hidden">
            <motion.div
              className="h-full bg-blue-500"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
              style={{ width: '40%' }}
            />
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
